
import React, { useState, useRef, useEffect } from 'react';
import { toast } from "@/components/ui/use-toast";
import { useVoiceInteraction } from '@/hooks/useVoiceInteraction';
import ChatHeader from './ChatHeader';
import MessageList from './MessageList';
import ChatInput from './ChatInput';
import { ChatMessageProps } from './ChatMessage';
import { AIAgent } from '@/hooks/useCareerChat';

export interface Message extends ChatMessageProps {
  id: string;
}

interface ChatInterfaceProps {
  onSendMessage: (message: string) => Promise<void>;
  messages: Message[];
  isTyping?: boolean;
  placeholder?: string;
  className?: string;
  agent?: AIAgent | null;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  onSendMessage,
  messages,
  isTyping = false,
  placeholder = "Type your message...",
  className,
  agent
}) => {
  const [isSending, setIsSending] = useState(false);
  const latestAssistantMessageRef = useRef<string>('');
  const prevMessagesLengthRef = useRef<number>(0);
  
  const { 
    isListening, 
    isSpeaking, 
    isVoiceEnabled,
    isAuthenticated,
    shouldAutoSpeak,
    voiceInputUsed,
    setShouldAutoSpeak,
    startListening, 
    stopListening, 
    setOnTranscriptReady,
    setVoiceInputUsed,
    speakText, 
    stopSpeaking,
    handleTranscriptSent
  } = useVoiceInteraction();
  
  // Set up speech recognition callback when component mounts
  useEffect(() => {
    // Handle recognized speech
    setOnTranscriptReady((text) => {
      if (text && text.trim() !== '') {
        console.log('Transcript ready, sending message:', text);
        handleSend(text);
        handleTranscriptSent(); // Signal that the transcript was sent
      }
    });
  }, []);
  
  // Track latest assistant message for TTS
  useEffect(() => {
    // Only process if we have messages
    if (messages.length === 0) return;
    
    const assistantMessages = messages.filter(msg => !msg.isUser);
    if (assistantMessages.length > 0) {
      const latestMessage = assistantMessages[assistantMessages.length - 1];
      
      // Check if this is a new message (messages array has grown)
      if (messages.length > prevMessagesLengthRef.current) {
        console.log('New message detected:', messages.length, 'vs', prevMessagesLengthRef.current);
        prevMessagesLengthRef.current = messages.length;
        
        // Check if this is a new assistant message
        if (latestMessage.content !== latestAssistantMessageRef.current) {
          latestAssistantMessageRef.current = latestMessage.content;
          
          console.log('Voice input used:', voiceInputUsed, 'Auto-speak:', shouldAutoSpeak);
          
          // Speak if auto-speak is enabled OR voice input was used to send the last message
          if ((shouldAutoSpeak || voiceInputUsed) && !isSpeaking && isAuthenticated) {
            console.log('Auto-speaking message because', 
              voiceInputUsed ? 'voice input was used' : 'auto-speak is enabled');
            
            speakText(latestMessage.content, agent?.elevenlabs_voice_id);
            
            // Reset the voice input flag after speaking
            if (voiceInputUsed) {
              console.log('Resetting voice input used flag after speaking');
              setTimeout(() => setVoiceInputUsed(false), 500);
            }
          }
        }
      }
    }
  }, [messages, isSpeaking, isAuthenticated, shouldAutoSpeak, voiceInputUsed, speakText, agent]);
  
  const handleSend = async (messageText: string) => {
    if (messageText.trim() === '' || isSending) return;
    
    console.log('Sending message:', messageText, 'Voice input used:', voiceInputUsed);
    setIsSending(true);
    
    try {
      await onSendMessage(messageText);
    } catch (error) {
      console.error("Error sending message:", error);
      toast({
        title: "Error",
        description: "Failed to send your message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSending(false);
    }
  };
  
  const toggleAutoSpeak = () => {
    if (!isAuthenticated) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to use voice features.",
        variant: "destructive"
      });
      return;
    }
    
    if (isSpeaking) {
      stopSpeaking();
    }
    setShouldAutoSpeak(!shouldAutoSpeak);
  };
  
  return (
    <div className={`flex flex-col h-full ${className}`} onClick={(e) => e.stopPropagation()}>
      <ChatHeader 
        title={agent?.name || "AI Career Agent"}
        shouldAutoSpeak={shouldAutoSpeak}
        toggleAutoSpeak={toggleAutoSpeak}
        isAuthenticated={isAuthenticated}
        agent={agent}
      />
      
      <MessageList 
        messages={messages}
        isTyping={isTyping}
        speakText={(text) => speakText(text, agent?.elevenlabs_voice_id)}
        isAuthenticated={isAuthenticated}
        agent={agent}
      />
      
      <ChatInput 
        onSendMessage={handleSend}
        placeholder={placeholder}
        isListening={isListening}
        isSending={isSending}
        isVoiceEnabled={isVoiceEnabled}
        startListening={startListening}
        stopListening={stopListening}
        isAuthenticated={isAuthenticated}
      />
    </div>
  );
};

export default ChatInterface;
